import { defineStore } from 'pinia';
import { api } from 'services/api';
import Account from '@models/Account';
import Device from '@models/Device';
import useSetTimeout from 'composables/set-timeout';

export const useSearchStore = defineStore({
  id: 'search',
  state: () => {
    const url = new URL(window.location.href);
    const terms = url.searchParams.get('terms') || '';

    return {
      accounts: [] as Account[],
      devices: [] as Device[],
      hasSearched: false,
      isInitializing: false,
      isSearching: false,
      lastSearchTerms: '',
      postSearchDelay: false,
      progress: 0,
      terms,
    };
  },
  getters: {
    accountsByKey(state) {
      const accountsByKey = {} as { [key: string]: Account };
      state.accounts.forEach((account) => {
        accountsByKey[account.key] = account;
      });
      return accountsByKey;
    },
    hasAccounts(state) {
      return Boolean(state.accounts.length);
    },
    hasDevices(state) {
      return Boolean(state.devices.length);
    },
  },
  actions: {
    clearResults() {
      this.accounts = [];
      this.devices = [];
      this.hasSearched = false;
      this.lastSearchTerms = '';
      this.postSearchDelay = false;
      this.progress = 0;
    },
    async search(newTerms: string, fromNav = false) {
      const wait = useSetTimeout();

      this.terms = newTerms.trim();

      if (!this.terms) {
        this.clearResults();
        return;
      }

      if (fromNav && this.lastSearchTerms === this.terms) {
        // Do not reload results if we're executing from a navigation
        return;
      }

      this.isInitializing = true;
      this.isSearching = true;
      this.clearResults();
      this.lastSearchTerms = this.terms;
      this.postSearchDelay = true;
      this.hasSearched = true;

      await api.get(`/search?terms=${this.terms}`);

      this.isSearching = false;

      await wait(1000);

      this.postSearchDelay = false;
    },
    setProgress(progress: number) {
      this.progress = progress;
      if (this.isInitializing && progress > 0) {
        this.isInitializing = false;
      }
    },
    addResult(type: 'account' | 'device', result: ApiAccount | ApiDevice) {
      if (type === 'account') {
        const apiAccount = <ApiAccount>result;
        const account = <Account>{
          deviceKeys: apiAccount.device_keys,
          key: apiAccount.key,
          name: apiAccount.nickname,
          ownerKey: apiAccount.owner_key,
          status: apiAccount.status,
        };

        const accountIndex = this.accounts.findIndex(({ key }) => key === account.key);
        const accountUpdate = {
          ...(accountIndex > -1 ? this.accounts[accountIndex] : {}),
          ...account,
        };

        const { searched_user: user } = apiAccount;

        if (!accountUpdate.searchedUsers) {
          accountUpdate.searchedUsers = [];
        }

        if (user && !accountUpdate.searchedUsers.find(({ key }) => key === user.key)) {
          accountUpdate.searchedUsers.push(user);
        }

        this.$patch((state) => {
          if (accountIndex > -1) {
            state.accounts[accountIndex] = accountUpdate;
          } else {
            state.accounts.push(accountUpdate);
          }
        });
      }

      if (type === 'device') {
        const apiDevice = <ApiDevice>result;
        const device = <Device>{
          key: apiDevice.key,
          serial: apiDevice.serial,
          modelName: apiDevice.model_name,
          accountKey: apiDevice.account_key,
          imei: apiDevice.imei,
          sim: apiDevice.sim,
          simActive: apiDevice.sim_active,
        };
        this.devices.push(device);
      }
    },
  },
});
